import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import DocumentFormSection from "./DocumentFormSection";
import { documentOutputFormatApi, documentTypeApi } from "@/services/api";
import { cmsApi } from "@/services/api/cmsApi";
import { DocumentOutputSection } from "@/models/DocumentOutputFormat";

interface DocumentFormBuilderProps {
  documentId: number;
  documentName?: string;
}

// Interface for template sections that can be used as sections
interface TemplatePage {
  id: number;
  name: string;
  title: string;
}

const DocumentFormBuilder = ({
  documentId,
  documentName,
}: DocumentFormBuilderProps) => {
  const { toast } = useToast();
  const { selectedRole } = useAuth();
  const [isAddingSection, setIsAddingSection] = useState(false);
  const [selectedTemplatePageId, setSelectedTemplatePageId] =
    useState<string>("");
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [isReordering, setIsReordering] = useState(false);

  // Fetch document details to get the document type and template ID
  const { data: documentData } = useQuery({
    queryKey: ["document", documentId],
    queryFn: async () => {
      // Dummy implementation - in real implementation this would fetch from API
      // For now, return a mock document with a document type ID
      return {
        id: documentId,
        name: documentName || `Document ${documentId}`,
        document_type_id: 9, // Mock document type ID
      };
    },
  });

  const documentTypeId = documentData?.document_type_id;

  // Fetch document type to get template ID
  const { data: documentType } = useQuery({
    queryKey: ["documentType", documentTypeId],
    queryFn: () => documentTypeApi.getById(documentTypeId!),
    enabled: !!documentTypeId,
  });

  const templateId = documentType?.template_id;

  // Fetch sections, subsections, and fields for the document
  const {
    data: sections = [],
    isLoading: loadingSections,
    refetch: refetchSections,
  } = useQuery({
    queryKey: ["documentOutputSections", documentId],
    queryFn: () => documentOutputFormatApi.getSections(documentId),
    enabled: !!documentId,
  });

  // Fetch template sections for section selection
  const { data: templatePages = [], isLoading: loadingTemplatePages } =
    useQuery({
      queryKey: ["templatePages", templateId],
      queryFn: async (): Promise<TemplatePage[]> => {
        if (!templateId) return [];

        // Fetch CMS content tree
        const allContent = await cmsApi.getContentTree();

        // Find the template folder
        const templateFolder = allContent.find(
          (item) => item.id === templateId
        );
        if (!templateFolder) return [];

        // Get all direct children of the template (layout, pages folder, etc.)
        const templateComponents = allContent.filter(
          (item) => item.parent_id === templateId
        );

        // Find the "Pages" folder within the template
        const pagesFolder = templateComponents.find((item) =>
          item.name.toLowerCase().includes("pages")
        );

        // Get all pages within the pages folder
        const pageItems = pagesFolder
          ? allContent.filter((item) => item.parent_id === pagesFolder.id)
          : [];

        // Combine layout and other direct components with pages
        const allTemplateItems = [
          ...templateComponents.filter((item) => item.type === "template"), // Layout and other templates
          ...pageItems, // Pages from the pages folder
        ];

        // Fetch translations for each item to get proper titles
        const itemsWithTitles = await Promise.all(
          allTemplateItems.map(async (item) => {
            try {
              const translations = await cmsApi.getContentTranslations(item.id);
              const englishTranslation = translations.find(
                (t) => t.language_code === "en"
              );
              return {
                id: item.id,
                name: item.name,
                title: englishTranslation?.title || item.name,
              };
            } catch (error) {
              return {
                id: item.id,
                name: item.name,
                title: item.name,
              };
            }
          })
        );

        return itemsWithTitles;
      },
      enabled: !!templateId,
    });

  // Filter out already used template sections from the available options
  const availableTemplatePages = templatePages.filter(
    (page) =>
      !sections.some(
        (section) =>
          section.template_page_id === page.id ||
          section.title.trim().toLowerCase() === page.title.trim().toLowerCase()
      )
  );

  const isLoading = loadingTemplatePages || loadingSections;

  const handleDragEnd = async (result: DropResult) => {
    const { destination, source, type } = result;

    if (!destination || destination.index === source.index) return;

    if (type === "sections" && sections.length > 0) {
      try {
        setIsReordering(true);

        const reorderedSections = Array.from(sections);
        const [movedSection] = reorderedSections.splice(source.index, 1);
        reorderedSections.splice(destination.index, 0, movedSection);

        // Update section orders in the database
        const sectionIds = reorderedSections.map((section) => section.id);
        await documentOutputFormatApi.reorderSections(documentId, sectionIds);

        toast({
          title: "Sections reordered",
          description: "The sections have been reordered successfully",
        });

        // Refetch data to get updated order
        await refetchSections();
      } catch (error: any) {
        toast({
          variant: "destructive",
          title: "Error reordering sections",
          description:
            error.message || "An error occurred while reordering the sections",
        });
      } finally {
        setIsReordering(false);
      }
    }
  };

  const handleAddSection = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!selectedTemplatePageId) {
      toast({
        variant: "destructive",
        title: "Please select a template section",
        description: "You must select a template section to create a section",
      });
      return;
    }

    try {
      setIsAddingSection(true);

      const selectedPage = availableTemplatePages.find(
        (p) => p.id.toString() === selectedTemplatePageId
      );
      if (!selectedPage) {
        throw new Error("Selected template section not found");
      }

      await documentOutputFormatApi.createSection(documentId, {
        title: selectedPage.title,
        section_order: sections.length,
        template_page_id: selectedPage.id,
      });

      toast({
        title: "Section added",
        description: `Section "${selectedPage.title}" has been added successfully.`,
      });

      setSelectedTemplatePageId("");
      setSectionDialogOpen(false);

      // Refetch sections to get the updated data
      await refetchSections();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error adding section",
        description:
          error.message || "An error occurred while adding the section",
      });
    } finally {
      setIsAddingSection(false);
    }
  };

  const openSectionDialog = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSectionDialogOpen(true);
  };

  const handleDeleteSection = async (sectionId: number) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this section? This will delete all subsections and fields in this section."
      )
    ) {
      return;
    }

    try {
      // Delete section using the API
      await documentOutputFormatApi.deleteSection(sectionId);

      toast({
        title: "Section deleted",
        description: "The section has been deleted successfully",
      });

      // Refetch sections to get the updated data
      await refetchSections();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error deleting section",
        description:
          error.message || "An error occurred while deleting the section",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="space-y-1">
          <CardTitle>Document Output Format</CardTitle>
          <CardDescription>
            Configure sections, subsections, and fields for generating the
            output document. Sections are based on template sections from your
            document type's template.
          </CardDescription>
        </div>
        <Button
          onClick={openSectionDialog}
          size="sm"
          disabled={!availableTemplatePages.length}
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Section
        </Button>
      </CardHeader>

      <CardContent className="space-y-6">
        {!templatePages.length ? (
          <div className="text-center p-8 border-2 border-dashed border-muted-foreground/25 rounded-lg">
            <p className="text-muted-foreground mb-2">
              No template sections available
            </p>
            <p className="text-sm text-muted-foreground">
              {!templateId
                ? "No template is configured for this document type. Please configure a template in Document Type settings."
                : "The configured template doesn't have any pages. Please add pages to the template in the CMS."}
            </p>
          </div>
        ) : sections.length === 0 ? (
          <div className="text-center p-8 border-2 border-dashed border-muted-foreground/25 rounded-lg">
            <p className="text-muted-foreground mb-4">
              No sections configured yet
            </p>
            <Button onClick={openSectionDialog} variant="outline">
              <Plus className="mr-2 h-4 w-4" />
              Add Your First Section
            </Button>
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable
              droppableId="document-sections"
              direction="vertical"
              type="sections"
            >
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="space-y-4"
                >
                  {sections.map((section, index) => (
                    <Draggable
                      key={`section-${section.id}`}
                      draggableId={`section-${section.id}`}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`${
                            snapshot.isDragging
                              ? "opacity-80 border-primary shadow-md"
                              : ""
                          }`}
                        >
                          <DocumentFormSection
                            section={section}
                            onSectionUpdated={async () => {
                              await refetchSections();
                            }}
                            onDeleteSection={handleDeleteSection}
                            dragHandleProps={provided.dragHandleProps}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </CardContent>

      {/* Add Section Dialog */}
      <Dialog open={sectionDialogOpen} onOpenChange={setSectionDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add Section from Template</DialogTitle>
            <DialogDescription>
              Select a template section to create a new section. The section
              will be named after the selected template section.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="template-page" className="text-right">
                Template section
              </label>
              <Select
                value={selectedTemplatePageId}
                onValueChange={setSelectedTemplatePageId}
                disabled={isAddingSection}
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select a template section" />
                </SelectTrigger>
                <SelectContent>
                  {availableTemplatePages.map((page) => (
                    <SelectItem key={page.id} value={page.id.toString()}>
                      {page.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setSectionDialogOpen(false)}
              disabled={isAddingSection}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddSection}
              disabled={isAddingSection || !selectedTemplatePageId}
            >
              {isAddingSection ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                "Add Section"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default DocumentFormBuilder;
